import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ClipboardCheck,
  Database,
  Mail,
  MessageSquare,
  Pencil,
  Phone,
  Plus,
  Trash2,
  UserPlus,
} from "lucide-react";
import api from "../../services/apiService";
import DataTable, { createActionsColumn, DataTableButton } from "../data-table";
import AlertNotification from "../notification";
import DeleteModal from "../models/deleteModal.jsx";
import ActivityLogModal from "./ActivityLogModal";

export const KPI_DEFINITIONS = [
  {
    key: "connectionsSent",
    label: "Connections Sent",
    icon: UserPlus,
    card: "border-l-blue-500 bg-blue-50/70 dark:bg-blue-500/10",
    iconWrap: "bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300",
  },
  {
    key: "inMailsSent",
    label: "InMails Sent",
    icon: MessageSquare,
    card: "border-l-sky-500 bg-sky-50/70 dark:bg-sky-500/10",
    iconWrap: "bg-sky-100 text-sky-600 dark:bg-sky-500/20 dark:text-sky-300",
  },
  {
    key: "emailsExtracted",
    label: "Emails Extracted",
    icon: Mail,
    card: "border-l-indigo-500 bg-indigo-50/70 dark:bg-indigo-500/10",
    iconWrap: "bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300",
  },
  {
    key: "directCalls",
    label: "Direct Calls",
    icon: Phone,
    card: "border-l-amber-500 bg-amber-50/70 dark:bg-amber-500/10",
    iconWrap: "bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300",
  },
  {
    key: "dataEmailsExtracted",
    label: "Data Emails Extracted",
    icon: Database,
    card: "border-l-teal-500 bg-teal-50/70 dark:bg-teal-500/10",
    iconWrap: "bg-teal-100 text-teal-600 dark:bg-teal-500/20 dark:text-teal-300",
  },
  {
    key: "screenerResponsesReceived",
    label: "Screener Responses",
    icon: ClipboardCheck,
    card: "border-l-purple-500 bg-purple-50/70 dark:bg-purple-500/10",
    iconWrap: "bg-purple-100 text-purple-600 dark:bg-purple-500/20 dark:text-purple-300",
  },
];

const formatDate = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
};

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: token ? `Bearer ${token}` : undefined };
};

function ActivityLogTab({ projectId }) {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const [showLogModal, setShowLogModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchLogs = useCallback(async () => {
    if (!Number.isFinite(projectId)) return;

    setIsLoading(true);
    try {
      const response = await api.get("/activity-logs", {
        params: { projectId },
        headers: authHeaders(),
      });
      setLogs(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch activity logs", error);
      setNotification({ text: "Failed to fetch activity logs.", status: false });
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const totals = useMemo(
    () =>
      logs.reduce((acc, log) => {
        KPI_DEFINITIONS.forEach(({ key }) => {
          acc[key] = (acc[key] || 0) + Number(log[key] || 0);
        });
        return acc;
      }, {}),
    [logs]
  );

  const handleOpenCreate = () => {
    setEditingLog(null);
    setSubmitError("");
    setShowLogModal(true);
  };

  const handleOpenEdit = (log) => {
    setEditingLog(log);
    setSubmitError("");
    setShowLogModal(true);
  };

  const handleCloseModal = () => {
    if (isSubmitting) return;
    setShowLogModal(false);
    setEditingLog(null);
    setSubmitError("");
  };

  const handleSubmitLog = async (values) => {
    setIsSubmitting(true);
    setSubmitError("");

    try {
      if (editingLog) {
        const response = await api.put(`/activity-logs/${editingLog.id}`, values, {
          headers: authHeaders(),
        });
        const updated = response?.data || {};
        setLogs((prev) =>
          prev.map((log) => (log.id === editingLog.id ? { ...log, ...updated } : log))
        );
        setNotification({ text: "Activity log updated.", status: true });
      } else {
        await api.post(
          "/activity-logs",
          { ...values, projectId },
          { headers: authHeaders() }
        );
        await fetchLogs();
        setNotification({ text: "Activity log saved.", status: true });
      }

      setShowLogModal(false);
      setEditingLog(null);
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to save activity log.";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (log) => {
    setLogToDelete(log);
    setShowDeleteModal(true);
  };

  const handleCloseDelete = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setLogToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!logToDelete) return;

    setIsDeleting(true);
    try {
      await api.delete(`/activity-logs/${logToDelete.id}`, { headers: authHeaders() });
      setLogs((prev) => prev.filter((log) => log.id !== logToDelete.id));
      setNotification({ text: "Activity log deleted.", status: true });
      setShowDeleteModal(false);
      setLogToDelete(null);
    } catch (error) {
      console.error("Failed to delete activity log", error);
      setNotification({ text: "Failed to delete activity log.", status: false });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      key: "date",
      label: "Date",
      sortable: true,
      searchable: false,
      render: (v) => <span className="font-medium text-app-primary">{formatDate(v)}</span>,
    },
    { key: "User.email", label: "Logged By", sortable: true, render: (v) => v || "-" },
    ...KPI_DEFINITIONS.map(({ key, label }) => ({
      key,
      label,
      sortable: true,
      searchable: false,
      align: "right",
      render: (v) => Number(v || 0),
    })),
    createActionsColumn(
      (log) => [
        { label: "Edit", icon: Pencil, hidden: !log.canEdit, onSelect: () => handleOpenEdit(log) },
        {
          label: "Delete",
          icon: Trash2,
          tone: "danger",
          hidden: !log.canDelete,
          onSelect: () => handleDeleteClick(log),
        },
      ],
      { getRowLabel: (log) => `activity on ${formatDate(log.date)}` }
    ),
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {KPI_DEFINITIONS.map(({ key, label, icon: Icon, card, iconWrap }) => (
          <div
            key={key}
            className={`flex min-w-0 flex-col justify-between rounded-xl border border-l-2 border-app-primary px-4 py-3 ${card}`}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="line-clamp-2 min-h-[2rem] text-[11px] font-semibold uppercase leading-4 tracking-wider text-slate-500 dark:text-slate-400">
                {label}
              </p>
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${iconWrap}`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
              </span>
            </div>
            <p className="mt-2 text-[28px] font-bold leading-none text-[#1e2a4a] dark:text-app-heading">
              {totals[key] || 0}
            </p>
          </div>
        ))}
      </div>

      <section className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold text-app-heading">Activity Log</h3>
          <p className="mt-1 text-sm text-app-muted">Daily outreach activity for this project.</p>
        </div>

        <DataTable
          ariaLabel="Activity log"
          columns={columns}
          data={logs}
          loading={isLoading}
          searchable
          searchPlaceholder="Search by logger"
          toolbarRight={
            <DataTableButton variant="primary" icon={Plus} onClick={handleOpenCreate}>
              Log Activity
            </DataTableButton>
          }
          defaultSort={{ key: "date", direction: "desc" }}
          pagination
          defaultPageSize={10}
          maxHeight="480px"
          labels={{ empty: "No activity logs found." }}
        />
      </section>

      {notification && (
        <AlertNotification
          text={notification.text}
          status={notification.status}
          onClose={() => setNotification(null)}
        />
      )}

      <ActivityLogModal
        key={editingLog?.id ?? "new"}
        show={showLogModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitLog}
        initialLog={editingLog}
        isSubmitting={isSubmitting}
        errorMessage={submitError}
      />

      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCloseDelete}
        handleConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this activity log?"
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeleting}
      />
    </div>
  );
}

export default ActivityLogTab;
